<script lang="ts">
	import { format } from 'svelte-i18n';
	import {
		AdvancedCardV2,
		ActionsContainer,
		Container,
		IconButton,
		LabelList,
		Text,
	} from '@Shelly/shelly-design-system';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { macFromId, real_id } from 'src/helpers/interface_helpers';
	import { getSensorsByBTHomeDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { push } from 'svelte-spa-router';
	import { getVirtualComponent } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { createEventDispatcher } from 'svelte';
	import type { Device } from 'src/types/Device';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';

	const dispatch = createEventDispatcher();

	export let type: string;
	export let device: Device;
	export let sensors: string[] = [];

	const deviceID = real_id(device.id);

	$: devicesWithMacAddress = Object.values($devices).map((device) => {
		let deviceWithMacAddr = device;
		if (!device?.addr) {
			deviceWithMacAddr.addr = (device?.mac || macFromId(device.id) || '')
				.toLowerCase()
				.match(/.{1,2}/g)
				.join(':');
		}
		return deviceWithMacAddr;
	});
</script>

<Text class="mb-s text-bold"
	>{type === 'temp'
		? $format(`text_attached_temp_sensor`, {
				default: 'Attached temperature sensor',
			})
		: $format(`text_attached_dw_sensors`, {
				default: 'Attached door/window sensors',
			})}
</Text>
<Container class="mb-s" padding="xs">
	{#each sensors as item, i (item)}
		{@const attachedDevice = devicesWithMacAddress.find(
			(newDevice) => newDevice?.addr === getSensorsByBTHomeDevice(device, item)?.addr,
		)}
		{@const sensor = getSensorsByBTHomeDevice(device, item)}
		{#if sensor}
			<AdvancedCardV2
				isFocusable={!!attachedDevice}
				class={i < sensors.length - 1 ? 'mb-s' : ''}
				title={attachedDevice ? attachedDevice.name : sensor.name}
				subtitle={{
					type: 'secondary',
					bold: false,
					textSmall: true,
					value: attachedDevice
						? $rooms[attachedDevice.room_id]?.name
							? $rooms[attachedDevice.room_id].name
							: capFirstChar($format('label_hidden', { default: 'Hidden' }))
						: sensor.addr,
				}}
				avatar={{
					image:
						attachedDevice?.image ||
						`images/device_images/${type === 'temp' ? 'SBHT-003C' : 'SBDW-002C'}.png`,
				}}
				on:click={() => {
					if (attachedDevice) {
						push(`/home/room/${attachedDevice.room_id}/devices`);
						setSidePanelState({
							type: 'device',
							id: attachedDevice.id,
							open: false,
						});
					}
				}}
			>
				<LabelList
					size="m"
					slot="main"
					labelList={sensor.list}
					data-prevent-drawer-swipe
					data-prevent-sidepanel-swipe
				/>
				<ActionsContainer orientation="vertical" type="simple" slot="right">
					{#if !attachedDevice}
						<IconButton
							aria-label={$format('button_edit_sensor', {
								default: 'Edit sensor',
							})}
							slot="first"
							variant="primary"
							icon="fas fa-gear"
							on:click={(e) => {
								e.stopPropagation();
								dispatch('change_sensor', {
									selectedSensor: getVirtualComponent(
										deviceID,
										sensor.bthomeDevice,
										attachedDevice?.name,
									),
									action: 'edit',
								});
							}}
						/>
					{/if}
					<IconButton
						aria-label={$format('button_delete_sensor', {
							default: 'Delete sensor',
						})}
						slot="second"
						icon="fas fa-trash"
						variant="danger"
						on:click={(e) => {
							e.stopPropagation();
							dispatch('change_sensor', {
								selectedSensor: getVirtualComponent(
									deviceID,
									sensor.bthomeDevice,
									attachedDevice?.name,
								),
								action: 'delete',
							});
						}}
					/>
				</ActionsContainer>
			</AdvancedCardV2>
		{/if}
	{:else}
		<Container type="glass" padding="s">
			<Text class="text-center"
				>{type === 'temp'
					? $format('text_no_attached_sensor', {
							default: 'No attached sensor yet',
						})
					: $format('text_no_attached_sensors', {
							default: 'No attached sensors yet',
						})}
			</Text>
		</Container>
	{/each}
</Container>
